import axios from 'axios';
import type { AxiosInstance } from 'axios';
import { ScreenAppClientOptions } from './types';
import { FilesAPI, TemplatesAPI, UploadAPI } from './api';

// Re-export all types for easier imports
export * from './types';
export * from './api/types';

/**
 * ScreenApp SDK Client
 * A browser-compatible SDK for interacting with the ScreenApp API
 */
class ScreenAppClient {
  private api: AxiosInstance;

  private baseUrl: string;

  private token: string;

  // Service instances
  public files: FilesAPI;

  public templates: TemplatesAPI;

  public upload: UploadAPI;

  constructor(options: ScreenAppClientOptions) {
    this.baseUrl = options.baseUrl || 'https://api.screenapp.io';
    this.token = options.token;

    this.api = axios.create({
      baseURL: this.baseUrl,
      headers: {
        Authorization: `Bearer ${this.token}`,
        'Content-Type': 'application/json',
      },
    });

    // Add response interceptor
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (!error || !error.response) {
          const er = {
            ...error,
            response: {
              ...error.response,
              data: {
                success: false,
                data: null,
                message: 'Something went wrong. Please try again later.',
              },
            },
          };
          return Promise.reject(er);
        }

        if (error.response.status === 401) {
          if (typeof error.response.data !== 'object') {
            const er = {
              ...error,
              response: {
                ...error.response,
                data: {
                  success: false,
                  data: null,
                  message: 'User unauthorized',
                },
              },
            };
            return Promise.reject(er);
          }
        } else if (error.response.status === 404) {
          if (typeof error.response.data !== 'object') {
            const er = {
              ...error,
              response: {
                ...error.response,
                data: {
                  success: false,
                  data: null,
                  message: 'Something went wrong. Please try again later.',
                },
              },
            };
            return Promise.reject(er);
          }
        }

        return Promise.reject(error);
      }
    );

    // Initialize API services
    this.files = new FilesAPI(this.api);
    this.templates = new TemplatesAPI(this.api);
    this.upload = new UploadAPI(this.api);
  }

  /**
   * Set the authentication token
   */
  setToken(token: string): void {
    this.token = token;
    this.api.defaults.headers.Authorization = `Bearer ${token}`;
  }

  /**
   * Protected method to get the API instance
   * This allows subclasses to access the API instance without exposing it publicly
   */
  protected getApiInstance(): AxiosInstance {
    return this.api;
  }
}

export default ScreenAppClient;
